import { Directive, ElementRef, HostListener } from '@angular/core';

/**
 * Diretiva para normalizar texto em campos de entrada.
 *
 * @Directive
 * @description Essa diretiva impede que o usuário insira espaços em branco
 * iniciais e finais, substitui múltiplos espaços por um único espaço e
 * remove caracteres invisíveis, mantendo a consistência no texto.
 *
 * @usageNotes Aplique essa diretiva a um elemento de entrada usando o seletor `appNormalizeText`.
 *
 * @example
 * <input type="text" appNormalizeText>
 */

@Directive({
  selector: '[appNormalizeText]'
})
export class NormalizeTextDirective {

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    const inputElement = this.el.nativeElement;
    const currentValue = inputElement.value;
    const normalizedValue = this.normalizeText(currentValue);

    if (currentValue !== normalizedValue) {
      const cursorPosition = inputElement.selectionStart;
      inputElement.value = normalizedValue;

      // Reposicionar o cursor considerando a normalização
      const newCursorPosition = this.calculateNewCursorPosition(currentValue, normalizedValue, cursorPosition);
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);

      // Disparar evento de input para atualizar o FormControl
      const inputEvent = new Event('input', { bubbles: true });
      inputElement.dispatchEvent(inputEvent);
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent): void {
    const inputElement = this.el.nativeElement;
    const currentValue = inputElement.value;
    const normalizedValue = this.normalizeText(currentValue).trim();

    if (currentValue !== normalizedValue) {
      inputElement.value = normalizedValue;

      // Disparar evento de input para atualizar o FormControl
      const inputEvent = new Event('input', { bubbles: true });
      inputElement.dispatchEvent(inputEvent);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    const pastedText = clipboardData?.getData('text') || '';
    const normalizedText = this.normalizeText(pastedText);

    const inputElement = this.el.nativeElement;
    const currentValue = inputElement.value;
    const startPos = inputElement.selectionStart;
    const endPos = inputElement.selectionEnd;

    // Inserir o texto normalizado na posição do cursor
    const newValue = currentValue.substring(0, startPos) + normalizedText + currentValue.substring(endPos);
    const finalValue = this.normalizeText(newValue);

    inputElement.value = finalValue;

    // Posicionar cursor após o texto colado
    const newCursorPosition = startPos + normalizedText.length;
    inputElement.setSelectionRange(newCursorPosition, newCursorPosition);

    // Disparar evento de input para atualizar o FormControl
    const inputEvent = new Event('input', { bubbles: true });
    inputElement.dispatchEvent(inputEvent);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const inputElement = this.el.nativeElement;
    const currentValue = inputElement.value;
    const cursorPosition = inputElement.selectionStart;

    // Permitir teclas de navegação e edição
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Escape',
      'Home', 'End', 'PageUp', 'PageDown'
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    // Prevenir múltiplos espaços consecutivos
    if (event.key === ' ') {
      // Verificar se já existe um espaço na posição anterior
      if (cursorPosition > 0 && currentValue[cursorPosition - 1] === ' ') {
        event.preventDefault();
        return;
      }

      // Prevenir espaço no início
      if (cursorPosition === 0) {
        event.preventDefault();
        return;
      }
    }
  }

  private normalizeText(text: string): string {
    if (!text) return text;

    // Remove caracteres invisíveis (unicode control characters)
    // Substitui múltiplos espaços por um único espaço
    return text
      .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, ' ');
  }

  private calculateNewCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // Se o valor não mudou, manter a posição
    if (oldValue === newValue) {
      return oldPosition;
    }

    // Calcular quantos caracteres foram removidos antes da posição do cursor
    const beforeCursor = oldValue.substring(0, oldPosition);
    const normalizedBefore = this.normalizeText(beforeCursor);

    return Math.min(normalizedBefore.length, newValue.length);
  }
}
